/** Temporal mask smoothing to reduce segmentation flicker. */

export const MASK_EMA_ALPHA = 0.22;
export const MASK_THRESHOLD = 0.48;
export const MASK_SOFT_BAND = 0.12;

/**
 * Blend a new segmentation frame into a persistent float mask (0–1).
 * @param {ImageData} raw - mask drawn at processing resolution
 * @param {Float32Array|null} smoothed - previous smoothed values
 * @param {number} alpha - EMA weight for the new frame
 */
export function erodeMask3x3(floatMask, width, height) {
  const out = new Float32Array(floatMask.length);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = y * width + x;
      let min = floatMask[i];
      for (let dy = -1; dy <= 1; dy += 1) {
        const ny = y + dy;
        if (ny < 0 || ny >= height) continue;
        for (let dx = -1; dx <= 1; dx += 1) {
          const nx = x + dx;
          if (nx < 0 || nx >= width) continue;
          min = Math.min(min, floatMask[ny * width + nx]);
        }
      }
      out[i] = min;
    }
  }
  return out;
}

export function blendSegmentationMask(raw, smoothed, alpha = MASK_EMA_ALPHA) {
  const count = raw.width * raw.height;
  const out = smoothed?.length === count ? smoothed : new Float32Array(count);
  const isFirst = !smoothed || smoothed.length !== count;
  const low = MASK_THRESHOLD - MASK_SOFT_BAND;
  const high = MASK_THRESHOLD + MASK_SOFT_BAND;
  const invBand = 1 / Math.max(0.001, high - low);

  for (let i = 0; i < count; i += 1) {
    const rawValue = raw.data[i * 4] / 255;
    const blended = isFirst ? rawValue : alpha * rawValue + (1 - alpha) * out[i];
    let value = blended;
    if (value <= low) {
      value = 0;
    } else if (value >= high) {
      value = Math.min(1, value);
    } else {
      const t = (value - low) * invBand;
      value = t * t * (3 - 2 * t) * blended;
    }
    out[i] = value;
  }
  return erodeMask3x3(out, raw.width, raw.height);
}

export function writeFloatMaskToImageData(floatMask, width, height) {
  const img = new ImageData(width, height);
  for (let i = 0; i < floatMask.length; i += 1) {
    const byte = Math.round(Math.min(1, Math.max(0, floatMask[i])) * 255);
    const o = i * 4;
    img.data[o] = byte;
    img.data[o + 1] = byte;
    img.data[o + 2] = byte;
    img.data[o + 3] = 255;
  }
  return img;
}
